'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { Box } from '@mui/material';

// Deep imports, not the '@/components' barrel — this renders on every route.
import { CategoryNavLinksSkeleton } from '@/components/skeletons/common/CategoryNavLinksSkeleton';
import { PillNavItem } from '@/components/specific/nav/PillNavItem';
import { useNavigationItems } from '@/hooks/useNavigationItems';

import { LAYOUT_MAX_WIDTH, PAGE_HORIZONTAL_PADDING, PrimaryFilter } from '@/constants';

type CategoryNavLinksProps = {
  isMoreOpen: boolean;
  moreButtonRef: React.RefObject<HTMLButtonElement>;
  onMoreClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMoreClose: () => void;
};

interface CategoryNavLinksItemProps {
  item: PrimaryFilter;
  isActive: boolean;
  isMoreOpen: boolean;
  moreButtonRef: React.RefObject<HTMLButtonElement>;
  onMoreClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onMoreClose: () => void;
}

const CategoryNavLinksItem = ({
  item,
  isActive,
  isMoreOpen,
  moreButtonRef,
  onMoreClick,
  onMoreClose,
}: CategoryNavLinksItemProps) => {
  const isNew = item.label === 'New';

  return (
    <Box key={item.label} sx={{ display: 'flex', alignItems: 'center' }}>
      <PillNavItem
        item={item}
        isActive={isActive}
        isMoreOpen={isMoreOpen}
        moreButtonRef={moreButtonRef}
        onMoreClick={onMoreClick}
        onMoreClose={onMoreClose}
      />
      {isNew && (
        <Box
          sx={{
            width: '1px',
            height: '20px',
            bgcolor: 'divider',
            mx: 0.5,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

export const CategoryNavLinks = ({
  isMoreOpen,
  moreButtonRef,
  onMoreClick,
  onMoreClose,
}: CategoryNavLinksProps) => {
  const pathname = usePathname();
  const { navItems, isLoading } = useNavigationItems();

  const [activeTab, setActiveTab] = useState<string>('');

  // Check if it's a specific market page
  const isMarketPage =
    pathname.startsWith('/markets/') ||
    pathname.startsWith('/live-markets/') ||
    (pathname.startsWith('/sports/') && pathname !== '/sports');

  if (!isMarketPage) {
    const matchingItem = navItems.find(
      (item) => item.path !== '/' && pathname.startsWith(item.path),
    );
    const newTab = (matchingItem ? matchingItem.path : pathname) || '/';

    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  } else if (pathname.startsWith('/sports') && activeTab !== '/sports') {
    setActiveTab('/sports');
  }

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (activeTabRef.current && scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const element = activeTabRef.current;
        const scrollLeft = element.offsetLeft - container.clientWidth / 2 + element.clientWidth / 2;
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    }, 100);
    return () => clearTimeout(timeout);
  }, [activeTab]);

  if (isLoading) {
    return <CategoryNavLinksSkeleton />;
  }

  const renderNavItem = (item: PrimaryFilter, index: number) => {
    const isFirstItemActive = index === 0 && activeTab === '/';
    const isActive = activeTab === item?.path || isFirstItemActive;

    return (
      <Box key={item?.label} ref={isActive ? activeTabRef : null}>
        <CategoryNavLinksItem
          item={item}
          isActive={isActive}
          isMoreOpen={isMoreOpen}
          moreButtonRef={moreButtonRef}
          onMoreClick={onMoreClick}
          onMoreClose={onMoreClose}
        />
      </Box>
    );
  };

  const renderedNavItemsList = navItems.map(renderNavItem);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0,
        position: 'relative',
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        width: '100%',
        px: PAGE_HORIZONTAL_PADDING,
      }}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          // Spread evenly across the bar, flush to both edges.
          justifyContent: 'space-between',
          gap: { xs: 0, lg: 0.5 },
          width: '100%',
          maxWidth: LAYOUT_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 0.5, sm: 1 },
          overflowX: 'auto',
          overflowY: 'visible',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {renderedNavItemsList}
      </Box>
    </Box>
  );
};

export default CategoryNavLinks;
